import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Save, X, Zap, ShieldCheck, Globe, RefreshCw,
  CheckCircle, AlertCircle, Key, Edit2
} from 'lucide-react';
import { apiCall, apiCallJson } from './Api';
import { PageLayout, PageHeader, PageBody } from './PageLayout';

/**
 * ApiConnectionsManager — tenant-level store of REUSABLE outbound API connections
 * that chatbot "API Call" flow nodes invoke with authentication.
 *
 * Secrets are write-only: the backend returns the sentinel '__set__' for any secret
 * that is configured, and this screen renders that as "configured — leave blank to keep".
 * A blank secret field on save keeps the stored value; typing a new value overwrites it.
 */

const AUTH_TYPES = [
  { value: 'none', label: 'No Auth' },
  { value: 'api_key', label: 'API Key (header or query)' },
  { value: 'bearer_static', label: 'Static Bearer Token' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'oauth2_client_credentials', label: 'OAuth2 — Client Credentials' },
  { value: 'session_login', label: 'Session / Login Token' },
];

const SECRET_SENTINEL = '__set__';

const emptyForm = () => ({
  id: null,
  name: '',
  description: '',
  base_url: '',
  auth_type: 'none',
  is_active: true,
  config: {},
});

const SecretField = ({ label, value, placeholder, onChange }) => {
  const configured = value === SECRET_SENTINEL;
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="password"
        value={configured ? '' : (value || '')}
        placeholder={configured ? '•••••••• (configured — leave blank to keep)' : (placeholder || '')}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        autoComplete="new-password"
      />
    </div>
  );
};

const TextField = ({ label, value, placeholder, onChange, mono }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      placeholder={placeholder || ''}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full border border-gray-300 rounded px-2 py-1 text-sm ${mono ? 'font-mono' : ''}`}
    />
  </div>
);

const ApiConnectionsManager = ({ user }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form object or null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [probeUrl, setProbeUrl] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiCallJson('/api/chatbot/api-connections');
      setConnections(data.connections || []);
    } catch (e) {
      setError(e.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing(emptyForm()); setTestResult(null); setProbeUrl(''); };
  const startEdit = (c) => {
    setEditing({
      id: c.id,
      name: c.name,
      description: c.description || '',
      base_url: c.base_url || '',
      auth_type: c.auth_type || 'none',
      is_active: c.is_active !== false,
      config: { ...(c.config || {}) },
    });
    setTestResult(null);
    setProbeUrl(c.base_url || '');
  };
  const cancel = () => { setEditing(null); setTestResult(null); setError(''); };

  const setCfg = (key, value) =>
    setEditing((f) => ({ ...f, config: { ...f.config, [key]: value } }));

  const save = async () => {
    if (!editing.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description,
        base_url: editing.base_url,
        auth_type: editing.auth_type,
        is_active: editing.is_active,
        config: editing.config || {},
      };
      if (editing.id) {
        await apiCallJson(`/api/chatbot/api-connections/${editing.id}`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
      } else {
        await apiCallJson('/api/chatbot/api-connections', {
          method: 'POST', body: JSON.stringify(payload),
        });
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete connection "${c.name}"? Flows referencing it will lose auth.`)) return;
    try {
      await apiCall(`/api/chatbot/api-connections/${c.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      let result;
      const body = JSON.stringify({ probe_url: probeUrl || null, probe_method: 'GET' });
      if (editing.id) {
        result = await apiCallJson(`/api/chatbot/api-connections/${editing.id}/test`, {
          method: 'POST', body,
        });
      } else {
        // Unsaved: send the config inline (secrets included) for a one-shot test.
        result = await apiCallJson('/api/chatbot/api-connections/test', {
          method: 'POST',
          body: JSON.stringify({
            auth_type: editing.auth_type,
            config: editing.config || {},
            probe_url: probeUrl || null,
            probe_method: 'GET',
          }),
        });
      }
      setTestResult(result);
    } catch (e) {
      setTestResult({ auth_ok: false, detail: e.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const renderAuthFields = () => {
    const cfg = editing.config || {};
    switch (editing.auth_type) {
      case 'api_key':
        return (
          <div className="space-y-2">
            <SecretField label="API Key" value={cfg.api_key} onChange={(v) => setCfg('api_key', v)} />
            <TextField label="Header / Param Name" value={cfg.api_key_name}
              placeholder="X-API-Key" onChange={(v) => setCfg('api_key_name', v)} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Send In</label>
              <select value={cfg.api_key_in || 'header'} onChange={(e) => setCfg('api_key_in', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
                <option value="header">Header</option>
                <option value="query">Query parameter</option>
              </select>
            </div>
          </div>
        );
      case 'bearer_static':
        return <SecretField label="Bearer Token" value={cfg.static_token}
          onChange={(v) => setCfg('static_token', v)} />;
      case 'basic':
        return (
          <div className="space-y-2">
            <TextField label="Username" value={cfg.username} onChange={(v) => setCfg('username', v)} />
            <SecretField label="Password" value={cfg.password} onChange={(v) => setCfg('password', v)} />
          </div>
        );
      case 'oauth2_client_credentials':
        return (
          <div className="space-y-2">
            <TextField label="Token URL" value={cfg.token_url}
              placeholder="https://idp.example.com/oauth/token" onChange={(v) => setCfg('token_url', v)} />
            <TextField label="Client ID" value={cfg.client_id} onChange={(v) => setCfg('client_id', v)} />
            <SecretField label="Client Secret" value={cfg.client_secret}
              onChange={(v) => setCfg('client_secret', v)} />
            <TextField label="Scope (optional)" value={cfg.scope}
              placeholder="read write" onChange={(v) => setCfg('scope', v)} />
            <TextField label="Audience (optional)" value={cfg.audience}
              onChange={(v) => setCfg('audience', v)} />
            <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
              The access token is fetched once and cached until it expires, then refreshed
              automatically. A 401 triggers a single transparent re-authentication.
            </p>
          </div>
        );
      case 'session_login':
        return (
          <div className="space-y-2">
            <TextField label="Login URL" value={cfg.login_url}
              placeholder="https://api.example.com/auth/login" onChange={(v) => setCfg('login_url', v)} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Login Body Format</label>
              <select value={cfg.login_content_type || 'json'} onChange={(e) => setCfg('login_content_type', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
                <option value="json">JSON</option>
                <option value="form">Form-encoded</option>
              </select>
            </div>
            <TextField label="Username" value={cfg.username} onChange={(v) => setCfg('username', v)} />
            <SecretField label="Password" value={cfg.password} onChange={(v) => setCfg('password', v)} />
            <TextField label="Token Path in Response" value={cfg.token_response_path}
              placeholder="result.token  ·  data.access_token  ·  token" mono
              onChange={(v) => setCfg('token_response_path', v)} />
            <div className="grid grid-cols-2 gap-2">
              <TextField label="Attach As Header" value={cfg.token_header}
                placeholder="Authorization" onChange={(v) => setCfg('token_header', v)} />
              <TextField label="Header Template (optional)" value={cfg.token_header_template}
                placeholder="Bearer {token}" mono onChange={(v) => setCfg('token_header_template', v)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <TextField label="Expiry Path (optional)" value={cfg.token_expiry_path}
                placeholder="expires_in" mono onChange={(v) => setCfg('token_expiry_path', v)} />
              <TextField label="TTL Seconds (fallback)" value={cfg.token_ttl_seconds}
                placeholder="3600" onChange={(v) => setCfg('token_ttl_seconds', v)} />
            </div>
            <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
              The token is extracted from the login response via the path above, cached, and
              re-fetched on expiry or a 401.
            </p>
          </div>
        );
      default:
        return <p className="text-xs text-gray-500">This connection sends no authentication.</p>;
    }
  };

  const renderConnectionForm = () => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
      <div className="flex items-center justify-between px-5 py-3 border-b">
        <h3 className="font-semibold text-gray-800 flex items-center">
          <Zap className="h-4 w-4 mr-2 text-yellow-600" />
          {editing.id ? 'Edit Connection' : 'New Connection'}
        </h3>
        <button onClick={cancel} className="text-gray-400 hover:text-gray-700" title="Close">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="p-5 space-y-3">
        <TextField label="Name" value={editing.name}
          placeholder="Orders API" onChange={(v) => setEditing((f) => ({ ...f, name: v }))} />
        <TextField label="Description (optional)" value={editing.description}
          onChange={(v) => setEditing((f) => ({ ...f, description: v }))} />
        <TextField label="Base URL (optional, used as default test target)" value={editing.base_url}
          placeholder="https://api.example.com" onChange={(v) => setEditing((f) => ({ ...f, base_url: v }))} />

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Authentication</label>
          <select value={editing.auth_type}
            onChange={(e) => setEditing((f) => ({ ...f, auth_type: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
            {AUTH_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          {renderAuthFields()}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={editing.is_active}
            onChange={(e) => setEditing((f) => ({ ...f, is_active: e.target.checked }))} />
          Active
        </label>

        <div className="border-t pt-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Test (optional probe URL)</label>
          <div className="flex gap-2">
            <input type="url" value={probeUrl} placeholder="https://api.example.com/ping"
              onChange={(e) => setProbeUrl(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
            <button onClick={runTest} disabled={testing}
              className="flex items-center gap-1 border border-blue-300 text-blue-700 px-3 py-1 rounded text-sm disabled:opacity-50">
              {testing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Test
            </button>
          </div>
          {testResult && (
            <div className={`mt-2 text-xs p-2 rounded border ${testResult.auth_ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
              <div className="flex items-center gap-1 font-medium">
                {testResult.auth_ok ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {testResult.detail}
              </div>
              {testResult.probe && (
                <div className="mt-1">
                  Probe: HTTP {testResult.probe.status} {testResult.probe.ok ? '✓' : '✗'}
                  {testResult.probe.error ? ` — ${testResult.probe.error}` : ''}
                  {testResult.probe.preview ? (
                    <pre className="mt-1 bg-white/60 p-1 rounded overflow-x-auto">{testResult.probe.preview}</pre>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 px-5 py-3 border-t">
        <button onClick={cancel} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <PageLayout>
      <PageHeader
        title="API Connections"
        icon={ShieldCheck}
        actions={
          <>
            <button onClick={load}
              className="flex items-center gap-1 border border-gray-300 text-gray-700 px-3 py-2 rounded text-sm">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={startCreate}
              className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded text-sm">
              <Plus className="h-4 w-4" /> New Connection
            </button>
          </>
        }
      />
      <PageBody maxWidth="max-w-5xl">
      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {editing && renderConnectionForm()}

      {loading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : connections.length === 0 && !editing ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <Globe className="h-10 w-10 mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 text-sm">No API connections yet.</p>
          <button onClick={startCreate} className="mt-3 text-blue-600 text-sm font-medium">
            Create your first connection
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {connections.map((c) => (
            <div key={c.id}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-800 truncate">{c.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex items-center gap-1">
                    <Key className="h-3 w-3" />
                    {(AUTH_TYPES.find((a) => a.value === c.auth_type) || {}).label || c.auth_type}
                  </span>
                  {!c.is_active && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">inactive</span>
                  )}
                </div>
                {c.base_url && <p className="text-xs text-gray-400 truncate">{c.base_url}</p>}
                {c.description && <p className="text-xs text-gray-500 truncate">{c.description}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => startEdit(c)}
                  className="p-2 text-gray-500 hover:text-blue-600" title="Edit">
                  <Edit2 className="h-4 w-4" />
                </button>
                <button onClick={() => remove(c)}
                  className="p-2 text-gray-500 hover:text-red-600" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </PageBody>
    </PageLayout>
  );
};

export default ApiConnectionsManager;
